import { sendAnalytics } from '@audio-network/analytics'
import { Tag } from '@audio-network/componentlibrary'
import { Explicit, Vocals } from '@audio-network/componentlibrary/icons'
import clsx from 'clsx'
import { useTranslation } from 'next-i18next'

import { NESTED_TRACKS_TRAY_ID } from '@/components/app/NestedTracks/NestedTracks.constants'
import { useIsNestedTracksEnabled } from '@/components/app/NestedTracks/NestedTracks.hooks'
import { showTrackInNestedTracksTray } from '@/components/app/NestedTracks/NestedTracks.utils'
import Link from '@/components/shared/Link'
import sessionStore from '@/stores/sessionStore'
import { sendAlgoliaInsightEvents } from '@/utils/algolia/sendAlgoliaInsightEvents'
import { hasStemsAccess } from '@/utils/hasStemsAccess'
import { shouldDisplayMixVariation } from '@/utils/music/shouldDisplayMixVariation'
import { trackHasStems } from '@/utils/trackHasStems'
import { getComposerUrl, getTrackUrl } from '@/utils/urls'

import styles from './TrackDetails.module.css'
import { TrackDetailsProps } from './TrackDetails.types'
import {
	ARTIST_NOUN,
	MIXES_NOUN,
	SEARCH_SOURCE,
	STEMS_NOUN,
	TRACK_NOUN,
} from './TrackRow.constants'

const TrackDetails = ({ source, track }: TrackDetailsProps): JSX.Element => {
	const user = sessionStore((state) => state.session?.user)
	const { t } = useTranslation(['trackrow'])
	const nestedTracksEnabled = useIsNestedTracksEnabled()

	const showStemsTag = user?.id && hasStemsAccess(user) && trackHasStems(track)

	const handleTrackClick = (noun: string): void => {
		sendAnalytics({
			noun,
			pastTenseVerb: 'Clicked',
			eventParameters: {
				TrackId: track.id,
				TrackTitle: track.title,
				TrackMixCount: track.mixCount,
				TrackHasStems: trackHasStems(track),
				TrackSource: source,
			},
		})

		if (source === SEARCH_SOURCE) {
			sendAlgoliaInsightEvents({
				eventType: 'clickedObjectIDsAfterSearch',
				eventName: 'Track Clicked',
				track,
			})
		}
	}

	const handleMixesButtonClick = (): void => {
		showTrackInNestedTracksTray(track, source, 'mixes')
		handleTrackClick(MIXES_NOUN)
	}

	const handleArtistClick = (composer: Composer): void => {
		sendAnalytics({
			noun: ARTIST_NOUN,
			pastTenseVerb: 'Clicked',
			eventParameters: {
				ArtistId: composer.id,
				ArtistName: composer.name,
				ArtistSource: source,
			},
		})
	}

	const trackPageURL = getTrackUrl(track)
	const trackNumber = `${track.album.number}/${track.albumTrackNumber}`

	const isStaff = user?.isStaff
	const displayMixVariation = shouldDisplayMixVariation(track)

	return (
		<div
			className={clsx(styles.details, !isStaff && styles.temporaryXXLDetails)}
			data-testid="track-details"
		>
			<span className={styles.titleGroup}>
				<Link
					className={clsx(styles.link, styles.title)}
					href={trackPageURL}
					onClick={() => handleTrackClick(TRACK_NOUN)}
					data-testid="track-row-title"
				>
					{track.title}
				</Link>
				<span className={styles.trackNumber}>{trackNumber}</span>
				{(track.explicit || track.vocal) && (
					<span className={styles.iconsGroup}>
						{track.explicit && (
							<Explicit
								data-testid="track-row-explicit-icon"
								title={t('is-explicit')}
								className={styles.icon}
							/>
						)}
						{track.vocal && (
							<Vocals title={t('is-vocal')} className={styles.icon} />
						)}
					</span>
				)}
			</span>
			<div className={styles.composersGroup} data-testid="track-row-artists">
				{track.originalComposer && (
					<span className={styles.originalComposer}>
						{track.originalComposer} - Arr.
					</span>
				)}
				{track.composers.map((composer) => (
					<Link
						href={getComposerUrl(composer)}
						key={composer.id}
						onClick={() => handleArtistClick(composer)}
						className={clsx(styles.link, styles.composer)}
					>
						{composer.name} ({composer.collectionSociety})
					</Link>
				))}
			</div>
			<span className={styles.tagsGroup}>
				{track.mixCount >= 2 &&
					(nestedTracksEnabled ? (
						// NOTE: This is temporary button that mirrors the behavior of the Tag component, but opens the nested tracks tray instead of navigating to the track page.
						<button
							type="button"
							className={clsx(styles.tag, styles.tagButton)}
							onClick={handleMixesButtonClick}
							data-testid="track-details-mixes-tag"
							{...{ command: 'show-modal', commandfor: NESTED_TRACKS_TRAY_ID }}
						>
							{t('mixes', { mixCount: track.mixCount })}
						</button>
					) : (
						<Tag
							className={styles.tag}
							href={`${trackPageURL}?initialTab=mixes`}
							onClick={() => handleTrackClick(MIXES_NOUN)}
							data-testid="track-details-mixes-tag"
						>
							{t('mixes', { mixCount: track.mixCount })}
						</Tag>
					))}
				{showStemsTag && (
					<Tag
						className={styles.tag}
						href={`${trackPageURL}?initialTab=stems`}
						data-testid="track-details-stems-tag"
						onClick={() => handleTrackClick(STEMS_NOUN)}
					>
						{t('stems')}
					</Tag>
				)}
			</span>
			<span className={styles.description} data-testid="track-row-description">
				{displayMixVariation && (
					<span className={styles.mixVariation}>{track.mixVariation}</span>
				)}
				{track.description}
			</span>
		</div>
	)
}

export default TrackDetails
